import { computed, inject, signal, Service } from '@angular/core';
import { InstructorAsset } from '@thiememeulenhoff/types';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { ThamService } from '../tham';

@Service()
export class ZipService {
  readonly #tham = inject(ThamService);

  readonly #fileName = signal('');

  readonly #status = signal<'idle' | 'downloading' | 'zipping' | 'finished'>(
    'idle',
  );

  readonly #zipProgress = signal(0);

  readonly #totalFilesToDownload = signal(0);

  readonly #totalDownloadedFiles = signal(0);

  readonly fileName = computed(() => this.#fileName());

  readonly status = computed(() => this.#status());

  readonly isFinished = computed(() => this.#status() === 'finished');

  readonly inProgress = computed(() =>
    ['downloading', 'zipping'].includes(this.#status()),
  );

  readonly zipProgress = computed(() => this.#zipProgress());

  readonly downloadProgress = computed(() => {
    if (this.#totalFilesToDownload() === 0) {
      return 0;
    }

    return Math.floor(
      (this.#totalDownloadedFiles() / this.#totalFilesToDownload()) * 100,
    );
  });

  #currentZip: JSZip | null = null;

  #abortController: AbortController | null = null;

  constructor() {
    window.onbeforeunload = (event) => {
      if (this.inProgress()) {
        event.preventDefault();
      }
    };
  }

  downloadAndZipFilesByAssets(
    assets: Array<InstructorAsset>,
    filename: string,
  ) {
    // Abort current download if there is one in progress.
    this.#abortController?.abort();

    this.#totalFilesToDownload.set(assets.length);
    this.#totalDownloadedFiles.set(0);
    this.#fileName.set(filename);
    this.#status.set('downloading');

    this.#currentZip = new JSZip();
    const localZip = this.#currentZip;

    this.#abortController = new AbortController();

    const fetchPromises = this.#getFetchPromises(assets);

    return Promise.all(fetchPromises)
      .then(() => {
        this.#status.set('zipping');

        return localZip.generateAsync({ type: 'blob' }, (metadata) => {
          this.#zipProgress.set(Math.floor(metadata.percent));
        });
      })
      .then((content) => {
        if (content) {
          saveAs(content, `${filename}.zip`);
        }
        this.#status.set('finished');
      })
      .catch(() => {
        // Handle user cancellation
        this.#status.set('idle');
      })
      .finally(() => {
        this.#currentZip = null;
        this.#abortController = null;
      });
  }

  cancelDownload() {
    this.#abortController?.abort();
  }

  #getFetchPromises(assets: Array<InstructorAsset>) {
    if (!this.#currentZip) {
      throw new Error('Current zip instance is not initialized');
    }

    const zip = this.#currentZip;

    return assets.map((asset) =>
      fetch(this.#tham.replaceThamDomainWithProxyCdn(asset.url), {
        signal: this.#abortController?.signal,
      })
        .then((response) => response.blob())
        .then((blob) => {
          zip.file(`${asset.name}.${asset.extension}`, blob);
          this.#totalDownloadedFiles.update((count) => count + 1);
        }),
    );
  }
}
